import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import markerGreen from '../../icons/marker_green.png';
import './style.css';
import markerRed from '../../icons/marker_red.png';
import { Location } from '../../types/types';

const containerStyle = {
    width: '100%',
    height: '400px',
};

const center = {
    lat: -23.548,
    lng: -46.638,
};

interface Coordinates {
    lat: number;
    lng: number;
}

const haversineDistance = (coords1: Coordinates, coords2: Coordinates): number => {
    const toRad = (value: number) => (value * Math.PI) / 180;

    const R = 6371;
    const dLat = toRad(coords2.lat - coords1.lat);
    const dLng = toRad(coords2.lng - coords1.lng);
    const lat1 = toRad(coords1.lat);
    const lat2 = toRad(coords2.lat);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
};

const mapStyles = [
    {
        featureType: 'poi',
        stylers: [{ visibility: 'off' }],
    },
    {
        featureType: 'poi.business',
        stylers: [{ visibility: 'off' }],
    },
    {
        featureType: 'poi.attraction',
        stylers: [{ visibility: 'off' }],
    },
];

interface MapComponentProps {
    apiKey: string;
    establishmentAddresses: Location[];
    setClosestLocation: (location: Location) => void;
    location?: string | null;
}

const MapComponent: React.FC<MapComponentProps> = ({ apiKey, establishmentAddresses, setClosestLocation, location = null }) => {
    const [, setCurrentPosition] = useState<Coordinates | null>(null);
    const [establishmentPositions, setEstablishmentPositions] = useState<Coordinates[]>([]);
    const [closestEstablishment, setClosestEstablishment] = useState<Coordinates | null>(null);
    const [recheck, setRecheck] = useState(false);

    useEffect(() => {
        setRecheck(false);
        if (typeof window.google?.maps?.Geocoder !== 'function') {
            setTimeout(() => setRecheck(true), 100);
            return () => {};
        }
        let positions: Coordinates[] = establishmentAddresses.map((u) => {
            return { lat: parseFloat(u.lat as string), lng: parseFloat(u.lon as string) };
        });
        setEstablishmentPositions(positions);
        navigator.geolocation.getCurrentPosition((position) => {
            const userPosition: Coordinates = {
                lat: parseFloat(position.coords.latitude as any),
                lng: parseFloat(position.coords.longitude as any),
            };
            setCurrentPosition(userPosition);
            findClosestEstablishment(userPosition, positions);
        });

        const findClosestEstablishment = (userPosition: Coordinates, positions: Coordinates[]) => {
            let closest: Coordinates | null = null;
            let minDistance = Infinity;

            for (const position of positions) {
                const distance = haversineDistance(userPosition, position);
                if (distance < minDistance) {
                    minDistance = distance;
                    closest = position;
                }
            }
            if (closestEstablishment === null || location === null) {
                setClosestEstablishment(closest);
                positions.forEach((position, index) => {
                    if (position === closest) {
                        setClosestLocation(establishmentAddresses[index]);
                    }
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recheck]);

    useEffect(() => {
        let index = 0;
        establishmentAddresses.forEach((e, i) => {
            if (e.api === location) {
                index = i;
            }
        });
        setClosestEstablishment(establishmentPositions[index]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, establishmentAddresses]);

    return (
        <LoadScript googleMapsApiKey={apiKey}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={closestEstablishment || center}
                zoom={15}
                options={{ styles: mapStyles }}
            >
                {establishmentPositions.map((position, index) => (
                    <Marker
                        key={index}
                        position={position}
                        label={establishmentAddresses[index]['nome']}
                        icon={{ url: position === closestEstablishment ? markerGreen : markerRed }}
                    />
                ))}
            </GoogleMap>
        </LoadScript>
    );
};

export default MapComponent;
